import React, { useMemo, useCallback, useEffect } from "react"
import { useFormikContext } from "formik"
import metadata from "libphonenumber-js/metadata.min.json"
import {
  getCountryCallingCode,
  type Country as CountryCode
} from "react-phone-number-input"
import { SelectField } from "../SelectField/SelectField"
import { TextField } from "../TextField/TextField"

/**
 * Type definition
 */
interface CountryOption {
  label: string
  value: string
  code: string
}

interface PhoneInputFieldProps {
  countryFieldName?: string
  numberFieldName: string
  defaultCountryCode?: string
}

/**
 * Utility: generate all E.164 country codes from metadata
 */
function generateAllCountries(): CountryOption[] {
  const countries = Object.keys(metadata.countries)
  return countries
    .map((code) => {
      const dialCode = getCountryCallingCode(code as CountryCode)
      const flag = getFlagEmoji(code)
      return {
        label: `${flag} +${dialCode} (${code})`,
        value: `${code}|+${dialCode}`,
        code
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Utility: convert ISO country code → flag emoji
 */
function getFlagEmoji(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

function encodePhoneNumber(dialCode: string, phoneNumber: string): string {
  if (!dialCode || !phoneNumber) return ""

  let cleanNumber = phoneNumber.replace(/\D/g, "")
  if (cleanNumber.startsWith("0")) {
    cleanNumber = cleanNumber.substring(1)
  }

  const e164Format = `${dialCode}${cleanNumber}`
  return encodeURIComponent(e164Format)
}

const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
  countryFieldName = "countryCode",
  numberFieldName,
  defaultCountryCode = "US"
}) => {
  const { values, setFieldValue } = useFormikContext<any>()

  const countryOptions = useMemo<CountryOption[]>(() => {
    return generateAllCountries()
  }, [])

  // Set default country on mount
  useEffect(() => {
    if (!values[countryFieldName]) {
      try {
        // Try the provided default country code
        const dialCode = getCountryCallingCode(
          defaultCountryCode as CountryCode
        )
        setFieldValue(countryFieldName, `${defaultCountryCode}|+${dialCode}`)
      } catch (error) {
        // If country code is invalid or not found, don't set any default
        // User will need to select a country
        console.warn(
          `Invalid country code: ${defaultCountryCode}, no default country set`
        )
      }
    }
  }, [defaultCountryCode, countryFieldName, setFieldValue, values])

  const countryValue = values[countryFieldName] || ""
  const phoneValue = values[`${numberFieldName}_raw`] || ""

  const handlePhoneChange = useCallback(
    (value: string, name: string, event: any) => {
      const rawPhone = event?.target?.value || value

      // Store raw value (TextField's useField hook will handle touched state)
      setFieldValue(`${numberFieldName}_raw`, rawPhone, true) // Validate this field

      if (countryValue && rawPhone) {
        const dialCode = countryValue.split("|")[1]
        const encoded = encodePhoneNumber(dialCode, rawPhone)
        // Store encoded value in the main field (for backend)
        setFieldValue(numberFieldName, encoded, false) // Don't validate this field
      } else {
        setFieldValue(numberFieldName, "", false)
      }
    },
    [countryValue, numberFieldName, setFieldValue]
  )

  const handleCountryChange = useCallback(
    (value: string, name: string, event: any) => {
      const newCountryValue = value
      setFieldValue(countryFieldName, newCountryValue)

      if (phoneValue && newCountryValue) {
        const dialCode = newCountryValue.split("|")[1]
        const encoded = encodePhoneNumber(dialCode, phoneValue)
        // Store encoded value in the main field (for backend)
        setFieldValue(numberFieldName, encoded, false) // Don't validate this field
      }
    },
    [phoneValue, numberFieldName, countryFieldName, setFieldValue]
  )

  return (
    <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
      <div style={{ width: "200px" }}>
        <SelectField
          name={countryFieldName}
          label="Country Code"
          options={countryOptions.map((c) => ({
            label: c.label,
            value: c.value
          }))}
          onChange={handleCountryChange}
          required={false}
        />
      </div>

      <div style={{ flex: 1 }}>
        <TextField
          name={`${numberFieldName}_raw`}
          label="Phone number"
          type="tel"
          required={true}
          onChange={handlePhoneChange}
        />
      </div>
    </div>
  )
}

export default PhoneInputField
